import React, { useState, useEffect, useCallback } from 'react'
import { ComboBox, ComboBoxChangeEvent, ComboBoxCloseEvent, ComboBoxFilterChangeEvent, ComboBoxFocusEvent, ComboBoxOpenEvent } from '@progress/kendo-react-dropdowns'
import { DeletedStateEnum, DealerIdsNamesDto } from '@v1DealersApi'
import { IPagedData, IDealerChangedEvent } from 'types'
import { toast } from 'core/toast'
import { V1ApiDealersClient } from 'core/http-common'
import { FieldWrapper } from '@progress/kendo-react-form'
import { Label, Error } from '@progress/kendo-react-labels'
import { hasStatusCode } from 'core/helpers'
import DefaultButton from 'components/Buttons/DefaultButton/DefaultButton'
import { Spinner } from 'components'

interface IProps {
    id: number,
    dealerId?: number,
    className?: string,
    label?: string,
    required?: boolean,
    touched?: boolean,
    allowDealerZero?: boolean
    withTimeZones?: boolean,
    defaultDeletedState?: undefined | DeletedStateEnum.Active | DeletedStateEnum.Deleted | DeletedStateEnum.Both,
    showActiveToggle?: boolean,
    onDealerChanged: (event: IDealerChangedEvent) => void,
    onDealerCleared: () => void,
}

const DealerSelect = (
    {
        dealerId,
        onDealerChanged,
        onDealerCleared,
        className,
        label,
        id,
        required,
        touched,
        allowDealerZero,
        withTimeZones,
        showActiveToggle,
        defaultDeletedState
    }: IProps) => {


    const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false)
    const errorId = showValidationMessage ? `${id}_error` : ''
    const labelId = label ? `${id}_label` : ''

    const [loadingDealers, setLoadingDealers] = useState<boolean>(false)
    const [loadingSelectedDealer, setLoadingSelectedDealer] = useState<boolean>(false)
    const [deletedState, setDeletedState] = useState<DeletedStateEnum | undefined>(defaultDeletedState)

    const [filter, setFilter] = useState<string>('')
    const [opened, setOpened] = useState<boolean>(false)
    const [selectedValue, setSelectedValue] = useState<DealerIdsNamesDto | null>(null)
    const [defaultValue, setDefaultValue] = useState<DealerIdsNamesDto | undefined>(undefined)
    const [data, setData] = useState<IPagedData<DealerIdsNamesDto>>({
        loaded: false,
        items: [],
        page: 1,
        pageSize: 100,
        skip: 0,
        totalItems: 0,
        errors: []
    })

    const fetchDealer = async () => {
        if (dealerId === undefined)
            return

        if (dealerId === 0) {
            if (defaultDeletedState)
                return

            setDeletedState(DeletedStateEnum.Active)
            return
        }

        try {
            setLoadingSelectedDealer(true)

            const dealer = withTimeZones
                ? await V1ApiDealersClient.getDealerIdNameAndTimeZone(dealerId, DeletedStateEnum.Both)
                : await V1ApiDealersClient.getDealerIdAndName(dealerId, DeletedStateEnum.Both)

            if (dealer && dealer.formattedName) {
                setFilter(dealer.formattedName)
                setDefaultValue(dealer)
                setSelectedValue(dealer)

                if (defaultDeletedState)
                    return

                if (dealer.deleted)
                    setDeletedState(DeletedStateEnum.Deleted)
                else
                    setDeletedState(DeletedStateEnum.Active)
            }
        }
        catch (e) {
            if (hasStatusCode(e, 404)) {
                let event: IDealerChangedEvent = {
                    dealerId: undefined,
                    name: undefined
                }
                onDealerChanged(event)
                return
            }
            if (dealerId == 0) {
                toast.warn('This Case is not assigned to a dealer.')
            } else {
                toast.responseErrors(e)
            }
        }
        finally {
            setLoadingSelectedDealer(false)
        }
    }

    const fetchDealers = async (filter: string, deletedState: DeletedStateEnum | undefined) => {
        try {
            setLoadingDealers(true)
            let response
            if (withTimeZones) {
                response = await V1ApiDealersClient.getDealerIdsNamesAndTimesZones(
                    deletedState || defaultDeletedState || DeletedStateEnum.Active,
                    filter,
                    data.page,
                    data.pageSize
                )
            } else {
                response = await V1ApiDealersClient.getDealerIdsAndNamesWithPossibleZero(
                    deletedState || defaultDeletedState || DeletedStateEnum.Active,
                    filter,
                    data.page,
                    data.pageSize,
                    null,
                    undefined,
                    null,
                    allowDealerZero
                )
            }

            if (response && response.totalItems && response.totalItems > 0) {
                setData({
                    loaded: true,
                    items: response.data,
                    page: response.page,
                    pageSize: response.pageSize,
                    skip: response.skip,
                    totalItems: response.totalItems,
                    errors: []
                })
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        finally {
            setLoadingDealers(false)
        }
    }

    useEffect(() => {
        fetchDealer()
    }, [dealerId])

    useEffect(() => {
        fetchDealers(filter, deletedState)
    }, [filter, deletedState])

    useEffect(() => {
        if (touched && required && !selectedValue)
            setShowValidationMessage(true)
    }, [touched])

    const handleOnChange = async (e: ComboBoxChangeEvent) => {

        if (!e.target.value) {
            setSelectedValue(null)
            setFilter('')
        }
        else {
            setSelectedValue(e.value)
            onDealerChanged({
                dealerId: e.value.dealerId,
                name: e.value.formattedName
            } as IDealerChangedEvent)
        }

        if (required) {
            setShowValidationMessage(!e.target.value)
        }
    }

    const handleOnClose = async (e: ComboBoxCloseEvent) => {
        setOpened(false)
        if (!e.target.value) {
            setSelectedValue(null)
            setFilter('')
        }
        else {
            setSelectedValue(e.target.value)
            onDealerChanged({
                dealerId: e.target.value.dealerId,
                name: e.target.value.formattedName
            } as IDealerChangedEvent)
        }
        if (required) {
            setShowValidationMessage(!e.target.value)
        }
    }

    const handleOnOpen = (e: ComboBoxOpenEvent) => {
        setOpened(true)
    }

    const handleFocus = (e: ComboBoxFocusEvent) => {
        setOpened(true)
    }

    const handleFilterChange = async (e: ComboBoxFilterChangeEvent) => {
        if (e.filter.value.length === 0) {
            onDealerCleared()
            return
        }
        if (e.filter.value.length >= 2)
            setFilter(e.filter.value)
    }

    const onToggleActive = () => {
        setDeletedState(deletedState === DeletedStateEnum.Active ? DeletedStateEnum.Deleted : DeletedStateEnum.Active)
        setSelectedValue(null)
        setFilter('')
        onDealerChanged({ dealerId: undefined, name: undefined } as IDealerChangedEvent)
    }

    return (
        <FieldWrapper>
            {label &&
                <Label
                    id={labelId}
                    editorValid={!showValidationMessage}
                    editorDisabled={showValidationMessage}
                >
                    {required &&
                        <span className='text-danger'>*</span>
                    }
                    {label} &nbsp;
                    {
                        showValidationMessage &&
                        <Error id={errorId}>This field is required.</Error>
                    }
                </Label>
            }

            <div className='k-form-field-wrap' style={{ position: 'relative' }}>
                <div className='input-group'>
                    <ComboBox
                        className={className}
                        required={required && showValidationMessage}
                        dataItemKey='formattedName'
                        textField='formattedName'
                        style={{ minWidth: '250px', flex: '1' }}
                        popupSettings={{ height: '250px' }}
                        filterable
                        data={data.items}
                        value={selectedValue}
                        defaultValue={defaultValue}
                        opened={opened}
                        onFocus={handleFocus}
                        onOpen={handleOnOpen}
                        onChange={handleOnChange}
                        onClose={handleOnClose}
                        onFilterChange={handleFilterChange}
                        loading={loadingDealers || loadingSelectedDealer}
                        suggest={true}
                    />
                    {showActiveToggle
                        &&
                        <div className='input-group-append'>
                            <DefaultButton
                                className='btn btn-default'
                                type='button'
                                onClick={onToggleActive}
                                disabled={loadingDealers}
                                title={deletedState === DeletedStateEnum.Active
                                    ? 'Showing only active dealer. Click to show deleted dealers.'
                                    : 'Showing only deleted dealers. Click to show active dealers.'
                                }
                                style={{
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0
                                }}
                            >
                                {deletedState === undefined
                                    ? <Spinner size='sm' center />
                                    : <>
                                        {deletedState === DeletedStateEnum.Active ? 'Active' : 'Deleted'}
                                    </>
                                }
                            </DefaultButton>
                        </div>
                    }
                </div>
            </div>
        </FieldWrapper>
    )
}

DealerSelect.defaultProps = {
    className: '',
    withTimeZones: false,
    showDeletedDealers: false
}

export default DealerSelect
